import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Col } from 'react-bootstrap'
import { JobListings } from '../components/careers/JobListing'
import JobCard from '../components/careers/JobCard'
import {
  CareerContentWrapper,
  CareerFilterCard,
  CareerHeaderSubtitle,
  CareerHeaderTitle,
  CareerNoVacanciesContainer,
  CareerNoVacanciesMessage,
  CareerPageResponsiveContainer,
  FooterContent,
} from '../components/styled'
import theme from '../constants/theme'
import LanguagesDropdown from '../components/languageDropdown'
import FullLogo from '../assets/logos/full-logo.png'
import AbstractForm from '../components/forms/AbstractForm'
import { useParams } from 'react-router-dom'
import apiCompanyPositions from '../services/api/companyPosition'
import PrimaryButton from '../components/buttons/PrimaryButton'
import LoadingPage from '../components/loadingPage'
import Inbox from '../assets/svgs/inbox'
import {
  filterByDatePosted,
  filterByBranch,
  filterByDepartment,
  filterByJobTitle,
  filterByLocation,
} from '../utilities/filters'
import { countryOptions } from '../utilities/countriesList'
import { PageBackground, StickyHeader } from './textInterview.style'

const DEFAULT_FILTERS = {
  jobTitle: '',
  branch: 'all',
  department: 'all',
  location: 'all',
  datePosted: 'all',
}

function Career() {
  const { t } = useTranslation()
  const { companyId } = useParams()
  const [, setLogo] = useState(null)
  const [positions, setPositions] = useState([])
  const [filteredPositions, setFilteredPositions] = useState([])
  const [jobSeekerPortalLinks, setJobSeekerPortalLinks] = useState(null)
  const [selectedPosition, setSelectedPosition] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [branchOptions, setBranchOptions] = useState([])
  const [departmentOptions, setDepartmentOptions] = useState([])
  const [filters, setFilters] = useState(DEFAULT_FILTERS)

  useEffect(() => {
    setIsLoading(true)
    apiCompanyPositions
      .getPublicActivePositions(companyId)
      .then((result) => {
        if (result.agency) {
          setJobSeekerPortalLinks({
            signUp: `${import.meta.env.VITE_JOB_SEEKER_PORTAL_URL}${result?.agency?.slug}/sign-up`,
            login: `${import.meta.env.VITE_JOB_SEEKER_PORTAL_URL}${result?.agency?.slug}/login`,
          })
          const agencyPositions = result.agency.positions.map((position) => ({
            ...position,
            isAgency: true,
          }))

          const organizationPositions = result.organizations.flatMap((org) =>
            org.positions.map((position) => ({
              ...position,
              isAgency: false,
            })),
          )

          const allPositions = [...agencyPositions, ...organizationPositions]
          setPositions(allPositions)
          setFilteredPositions(allPositions)
          setSelectedPosition(allPositions[0])
          extractFilterOptions(allPositions)
          if (result?.agency.logo) {
            setLogo(result?.agency.logo)
          }
        } else {
          setPositions(result?.company || [])
          setFilteredPositions(result?.company || [])
          setSelectedPosition((result?.company || [])[0])
          extractFilterOptions(result?.company || [])
          if (result?.company.logo) {
            setLogo(result?.company.logo)
          }
        }
      })
      // eslint-disable-next-line no-console
      .catch(console.log)
      .finally(() => setIsLoading(false))
  }, [companyId])

  useEffect(() => {
    if (!positions) return
    const allPositions = positions.map((position) => ({
      ...position,
      location: position.country,
    }))

    const filteredData = allPositions.filter((position) => {
      return (
        filterByJobTitle(position, filters.jobTitle) &&
        filterByBranch(position, filters.branch) &&
        filterByDepartment(position, filters.department) &&
        filterByLocation(position, filters.location) &&
        filterByDatePosted(position.createdAt, filters.datePosted)
      )
    })

    setFilteredPositions(filteredData)
    setSelectedPosition(null)
  }, [positions, filters])

  const extractFilterOptions = (positionsList) => {
    const branches = new Set()
    const departments = new Set()

    positionsList.forEach((position) => {
      if (position.branch?.name) {
        branches.add(position.branch.name)
      }
      if (position.department?.name) {
        departments.add(position.department.name)
      }
    })

    const branchOpts = [{ label: t('careers.all'), value: 'all' }]
    branches.forEach((branch) => {
      branchOpts.push({ label: branch, value: branch })
    })

    const departmentOpts = [{ label: t('careers.all'), value: 'all' }]
    departments.forEach((department) => {
      departmentOpts.push({ label: department, value: department })
    })

    setBranchOptions(branchOpts)
    setDepartmentOptions(departmentOpts)
  }

  const handleFilterChange = (updatedFilters) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...updatedFilters,
    }))
  }

  if (isLoading) {
    return <LoadingPage />
  }

  const getInitials = (name) => {
    if (!name) return ''
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase()
  }

  const formInputs = [
    {
      name: 'jobTitle',
      type: 'text',
      label: t('careers.jobTitle'),
      placeholder: t('careers.searchByTitleOrKeyword'),
      width: 4,
    },
    {
      name: 'branch',
      renderType: 'select',
      options: [
        { label: t('careers.allBranches'), value: 'all' },
        ...branchOptions.slice(1),
      ],
      label: t('careers.branch'),
      width: 4,
    },
    {
      name: 'department',
      renderType: 'select',
      options: [
        { label: t('careers.allDepartments'), value: 'all' },
        ...departmentOptions.slice(1),
      ],
      label: t('careers.department'),
      width: 4,
    },
    {
      name: 'location',
      renderType: 'select',
      options: [
        { label: t('careers.allLocations'), value: 'all' },
        ...countryOptions,
      ],
      label: t('careers.location'),
      width: 4,
    },
    {
      name: 'datePosted',
      renderType: 'select',
      options: [
        { label: t('careers.anyTime'), value: 'all' },
        { label: t('careers.lastMonth'), value: 'lastMonth' },
        { label: t('careers.lastWeek'), value: 'lastWeek' },
        { label: t('careers.past24Hours'), value: 'past24Hours' },
      ],
      label: t('careers.datePosted'),
      width: 4,
    },
  ]

  const companyName = positions?.[0]?.company?.companyName

  return (
    <PageBackground>
      <StickyHeader>
        <Col xs="auto">
          <img height="auto" width="150px" src={FullLogo} alt="" />
        </Col>
        <Col />

        <Col className="w-100 d-flex gap-2 align-items-center justify-content-end">
          <LanguagesDropdown />
          {jobSeekerPortalLinks && (
            <>
              <PrimaryButton
                color={theme.gray100}
                border={`1px solid ${theme.talentinoDarkerWhiteBlue}`}
                backgroundcolor={theme.white}
                hovercolor={theme.talentinoDarkerWhiteBlue}
                width="auto"
                className="text-nowrap"
                onClick={() =>
                  window.open(jobSeekerPortalLinks.login, '_blank')
                }
              >
                {t('careers.login')}
              </PrimaryButton>
              <PrimaryButton
                width="auto"
                className="text-nowrap"
                onClick={() =>
                  window.open(jobSeekerPortalLinks.signUp, '_blank')
                }
              >
                {t('careers.signUp')}
              </PrimaryButton>
            </>
          )}
        </Col>
      </StickyHeader>
      {positions.length > 0 ? (
        <CareerContentWrapper>
          <div className="mb-2">
            <CareerHeaderTitle>{t('careers.openPositions')}</CareerHeaderTitle>
            <CareerHeaderSubtitle>
              {t('careers.openPositionsSubtitle', {
                company: companyName || t('careers.ourCompany'),
                count: positions.length,
              })}
            </CareerHeaderSubtitle>
          </div>
          <CareerFilterCard>
            <AbstractForm
              className="w-100"
              inputs={formInputs}
              defaultValues={DEFAULT_FILTERS}
              handleFormWatch={handleFilterChange}
            />
          </CareerFilterCard>
          <CareerPageResponsiveContainer>
            <JobListings
              positions={filteredPositions || []}
              setSelectedPosition={setSelectedPosition}
              selectedPosition={selectedPosition}
              getInitials={getInitials}
            />
            <JobCard
              selectedPosition={selectedPosition}
              getInitials={getInitials}
            />
          </CareerPageResponsiveContainer>
        </CareerContentWrapper>
      ) : (
        <div className="w-100 d-flex justify-content-center align-items-center">
          <CareerNoVacanciesContainer>
            <Inbox width="50px" height="50px" />
            <CareerNoVacanciesMessage fontSize={18}>
              {t('careers.noOpenPositionsInThisOrganization')}
            </CareerNoVacanciesMessage>
          </CareerNoVacanciesContainer>
        </div>
      )}
      <FooterContent className="d-flex py-2 border-t border-gray-200">
        {t('footer.TalentinoAI')} © {new Date().getFullYear()}{' '}
        {t('footer.allRightsReserved')}
      </FooterContent>
    </PageBackground>
  )
}

export default Career
